"use client"

/**
 * Files on their way up, as tiles under the comment or note: a progress ring while uploading,
 * "Processing" while a video is converted, the reason when a file is refused (too big, too long,
 * not a photo or video). ✕ removes a tile.
 */
import { Camera, Image as ImageIcon, Loader2, Play, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import type { PendingUpload } from "./types"

const RING = 94.25 // 2πr for r = 15

export function PendingUploads({ items, onRemove }: { items: PendingUpload[]; onRemove: (id: string) => void }) {
  const { t } = useTranslation()
  if (!items.length) return null
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {items.map((u) => {
        const failed = u.state === "error"
        return (
          <div key={u.id} className="relative w-[72px]">
            <div className={cn("relative h-[72px] w-[72px] overflow-hidden rounded-xl bg-gray-100", failed && "ring-2 ring-inset ring-rose-400")}>
              {failed || !u.kind || (u.kind === "photo" && !u.displayable) ? (
                <span className="grid h-full w-full place-items-center text-gray-400">
                  {u.kind === "video" ? <Camera className="h-6 w-6" /> : <ImageIcon className="h-6 w-6" />}
                </span>
              ) : u.kind === "video" ? (
                <video src={`${u.previewUrl}#t=0.5`} muted playsInline preload="metadata" className="pointer-events-none h-full w-full object-cover" />
              ) : (
                <img src={u.previewUrl} alt="" className="pointer-events-none h-full w-full object-cover" />
              )}
              {u.state === "uploading" && (
                <span className="absolute inset-0 grid place-items-center bg-black/35">
                  <svg viewBox="0 0 36 36" className="h-9 w-9 -rotate-90">
                    <circle cx="18" cy="18" r="15" fill="none" strokeWidth="3.2" className="stroke-white/35" />
                    <circle cx="18" cy="18" r="15" fill="none" strokeWidth="3.2" strokeLinecap="round" className="stroke-white"
                            strokeDasharray={RING} strokeDashoffset={RING * (1 - u.progress)} />
                  </svg>
                </span>
              )}
              {u.state === "processing" && (
                <span className="absolute inset-0 grid place-content-center justify-items-center gap-1 bg-gray-50/85 text-[10.5px] font-semibold text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("attachments.processing", "Processing")}
                </span>
              )}
              {u.state === "ready" && u.kind === "video" && u.duration && (
                <span className="absolute bottom-1 left-1 inline-flex items-center gap-0.5 rounded-full bg-black/65 px-1.5 py-0.5 text-[10.5px] font-semibold text-white">
                  <Play className="h-2.5 w-2.5 fill-white" />{u.duration}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => onRemove(u.id)}
              className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full border-2 border-white bg-gray-900 text-white"
              aria-label={t("attachments.remove", "Remove")}
            >
              <X className="h-3 w-3" />
            </button>
            <div className={cn("mt-1 text-[10.5px] leading-tight", failed ? "text-rose-500" : "truncate text-gray-400")}>
              {failed ? u.error : u.name}
            </div>
          </div>
        )
      })}
    </div>
  )
}
